import { Divider, Paper, Stack, useMediaQuery } from "@mui/material";
import React from "react";
import {
  FormResultByIdRow,
  GetFormResultByIdDocument,
} from "@quales/data-access/gql-server";
import { Form } from "@bpipartners/rjsf-web-ui";
import { useTranslations } from "next-intl";
import validator from "@bpipartners/rjsf-validator-ajv";
import { CustomTypographyComponent } from "../../../ui/text";
import { useTheme } from "@quales/utils/theme";
import { useSnackbar } from "notistack";
import { useWindowDimensions } from "@quales/utils/hooks";
import { useQuery } from "@apollo/client";

interface ViewSubmittedFormCompositeProps {
  formID: string;
  useLiveValidate: boolean;
  inputFormData?: any;
  children?: any;
  hideFormTitle?: boolean;
  isReadOnly?: boolean;
  onFormvalueChanged?: (
    formvalue: any,
    submitted: boolean,
    formContent?: FormResultByIdRow
  ) => void;
  onSubmitClicked?: (
    formData: any,
    submitted: boolean,
    formVersion?: any
  ) => void;
}

export const ViewSubmittedFormComposite = React.forwardRef(
  (props: ViewSubmittedFormCompositeProps, ref) => {
    const {
      formID,
      useLiveValidate,
      inputFormData,
      onFormvalueChanged,
      children,
      onSubmitClicked,
      hideFormTitle,
      isReadOnly = false,
    } = props;

    const { data: formData, loading: formLoading } = useQuery(
      GetFormResultByIdDocument,
      {
        variables: {
          id: formID,
        },
      }
    );

    const t = useTranslations();
    const theme = useTheme();
    const submitFormRef = React.createRef();
    const { enqueueSnackbar } = useSnackbar();

    const smallDevice = useMediaQuery(theme.breakpoints.down(1090));
    const windowSize = useWindowDimensions();
    const [formContent, setFormContent] = React.useState<FormResultByIdRow>();
    const [useLiveValidateState, setUseLiveValidateState] =
      React.useState(false);

    const onSubmit = (val: any, e: any) => {
      if (onSubmitClicked) {
        onSubmitClicked(val.formData, true, formContent.formVersion);
      }
    };

    const onValueChanged = (val: any, error: any) => {
      //@ts-ignore
      setFormContent({
        ...formContent,
        formData: val.formData,
      });
      if (onFormvalueChanged) {
        onFormvalueChanged(val.formData, false, formContent);
      }
    };

    const handleOnError = (val) => {
      setTimeout(() => {
        enqueueSnackbar(t("feedback.missingValues"), {
          variant: "error",
        });
      }, 1000);
    };

    const transformErrors = (errors: any[]) =>
      errors.map((error: any) => {
        if (useLiveValidate && useLiveValidateState === false) {
          setUseLiveValidateState(true);
        }
        if (error.name === "required") {
          error.message = t("feedback.requiredField");
        }
        return error;
      });

    React.useEffect(() => {
      if (!formLoading && formData && formData.FormResultByID) {
        if (inputFormData) {
          setFormContent({
            formData: inputFormData,
            formID: formID,
            formVersion: formData.FormResultByID.formVersion,
            formSchema: formData.FormResultByID.formSchema,
            uiSchema:
              formContent && formContent.uiSchema
                ? formContent?.uiSchema
                : formData.FormResultByID.uiSchema,

            name: formData.FormResultByID.name,
          });
        } else {
          setFormContent({
            formData: formData.FormResultByID.formData,
            formID: formID,
            formVersion: formData.FormResultByID.formVersion,
            formSchema: formData.FormResultByID.formSchema,
            uiSchema:
              formContent && formContent.uiSchema
                ? formContent?.uiSchema
                : formData.FormResultByID.uiSchema,

            name: formData.FormResultByID.name,
          });
        }
      } else {
        setFormContent(undefined);
      }
    }, [inputFormData, formData, formLoading]);

    React.useImperativeHandle(ref, () => ({
      onSubmit() {
        if (onSubmitClicked) {
          //@ts-ignore
          submitFormRef.current.click();
        }
      },
    }));

    return (
      <Stack id="ViewSubmittedFormComposite">
        {!formLoading &&
          formContent &&
          formContent &&
          formContent.formSchema && (
            <Stack alignItems={smallDevice ? "center" : ""}>
              <Paper elevation={0}>
                {!hideFormTitle && (
                  <Stack padding={2} spacing={2}>
                    <CustomTypographyComponent variant="h4">
                      {formContent.name}
                    </CustomTypographyComponent>
                    <Divider />
                  </Stack>
                )}
                <Stack direction="row" spacing={2} padding={2}>
                  <Stack
                    height={
                      theme.dimensions.baseHeight
                        ? theme.dimensions.baseHeight - 280
                        : "80%"
                    }
                    spacing={2}
                    padding={2}
                    minWidth={200}
                    // width={smallDevice ? 700 : 1000}
                    overflow="auto"
                    width={"100%"}
                  >
                    <Form
                      schema={
                        formContent &&
                        formContent.formSchema !== undefined &&
                        formContent.formSchema &&
                        formContent.formSchema
                      }
                      uiSchema={
                        (formContent &&
                          formContent.uiSchema !== undefined &&
                          formContent.uiSchema) ||
                        {}
                      }
                      config={{
                        STORAGE_URL: process.env.STORAGE_URL || "/files",
                      }}
                      onSubmit={onSubmit}
                      readonly={isReadOnly}
                      onError={handleOnError}
                      noHtml5Validate={true}
                      onChange={onValueChanged}
                      liveValidate={useLiveValidateState}
                      formData={(formContent && formContent.formData) || {}}
                      ismobile={false}
                      transformErrors={transformErrors}
                      validator={validator}
                    >
                      {children && children}
                      <button
                        //@ts-ignore
                        ref={submitFormRef}
                        type="submit"
                        style={{ display: "none" }}
                      />
                    </Form>
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          )}
      </Stack>
    );
  }
);

ViewSubmittedFormComposite.displayName = "ViewSubmittedFormComposite";
export default ViewSubmittedFormComposite;
